import ScheduleQuiz from "../../../../../assets/icons/new-note.svg";
import AIIcon from "../../../../../assets/icons/magic-wand.svg";
import reportsIcon from "../../../../../assets/icons/shared-notes.svg";
import QuizIcon from "../../../../../assets/icons/quiz-icon-overview.svg";
import {
  Box,
  LinearProgress,
  MenuItem,
  Pagination,
  Select,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import React from "react";
import { useSelector } from "react-redux";
import CustomOverviewCard from "../../../../../customComponents/CustomOverviewCard";
import CustomClickableQuestionsCard from "../../../../../customComponents/CustomClickableQuestionsCard";
import { useGetQuizzesPaginatedQuery } from "../../../../../app/quizApiSlice";
import { useGetTeacherSubjectsQuery } from "../../../../../app/questionApiSlice";
import { selectCurrentUser } from "../../../auth/authSlice";
import { useGetSubjectsPaginatedQuery } from "../../../subjects/subjectsApiSlice";
import { useGetClassesPaginatedQuery } from "../../../classroom/classApiSlice";

const overviewCard = [
  { id: 1, icon: ScheduleQuiz, text: "Schedule Quiz", link: "new" },
  { id: 2, icon: AIIcon, text: "Create with AI", link: "ai" },
  { id: 3, icon: reportsIcon, text: "Quiz Reports", link: "report-list" },
];

const Quizzes = () => {
  const [sortBy, setSortBy] = React.useState("date");
  const [page, setPage] = React.useState(1);
  const [filterSubject, setFilterSubject] = React.useState("all");
  const [filterLevel, setFilterLevel] = React.useState("all");
  const [filterType, setFilterType] = React.useState("all");
  const itemsPerPage = 10;
  const navigate = useNavigate();
  const currentUser = useSelector(selectCurrentUser);
  const userRoles = currentUser?.roles?.map((r) => r?.role?.name).filter(Boolean) || [];
  const isAdmin = userRoles.includes("admin");
  const isTeacher = userRoles.includes("teacher");
  const isExaminer = userRoles.includes("examiner");

  // Show "Schedule Quiz" cards only for users who can create quizzes (examiner or teacher)
  const canCreateQuizzes = isExaminer || isTeacher;
  // Fetch all school data for admin or examiner (not restricted to their own subjects)
  const isAdminOrExaminer = isAdmin || isExaminer;

  const { data: teacherSubjectsData } = useGetTeacherSubjectsQuery(undefined, { skip: isAdminOrExaminer });
  const { data: subjectsData } = useGetSubjectsPaginatedQuery({ page: 1, per_page: 100 }, { skip: !isAdminOrExaminer });
  const { data: classroomsData } = useGetClassesPaginatedQuery({ page: 1, per_page: 100 }, { skip: !isAdminOrExaminer });

  const { data, isLoading, isFetching } = useGetQuizzesPaginatedQuery({
    page,
    per_page: itemsPerPage,
    subject: filterSubject !== "all" ? filterSubject : null,
    level: filterLevel !== "all" ? filterLevel : null,
    type: filterType !== "all" ? filterType : null,
    sort_by: sortBy,
  });

  const quizzes = data?.quizzes || [];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeSort = (event) => {
    setSortBy(event.target.value);
    setPage(1);
  };

  const handleFilterSubjectChange = (event) => {
    setFilterSubject(event.target.value);
    setPage(1);
  };

  const handleFilterLevelChange = (event) => {
    setFilterLevel(event.target.value);
    setPage(1);
  };

  const handleFilterTypeChange = (event) => {
    setFilterType(event.target.value);
    setPage(1);
  };

  const totalQuizzes = data?.pagination?.total || 0;
  const totalPages = data?.pagination?.pages || 0;

  const startIndex = (page - 1) * itemsPerPage + 1;
  const endIndex = Math.min(page * itemsPerPage, totalQuizzes);

  const uniqueSubjects = React.useMemo(() => {
    if (isAdminOrExaminer) {
      if (!subjectsData?.subjects) return [];
      return [...new Set(subjectsData.subjects.map((s) => s.name).filter(Boolean))].sort();
    }
    if (!teacherSubjectsData) return [];
    const subjects = teacherSubjectsData.map((classroom) => classroom.name);
    return [...new Set(subjects)].filter(Boolean).sort();
  }, [isAdminOrExaminer, teacherSubjectsData, subjectsData]);

  const uniqueLevels = React.useMemo(() => {
    if (isAdminOrExaminer) {
      if (!classroomsData?.classrooms) return [];
      const levels = classroomsData.classrooms.map((c) => c.level);
      return [...new Set(levels)].filter(Boolean).sort();
    }
    if (!teacherSubjectsData) return [];
    const levels = teacherSubjectsData.map((classroom) => classroom.level);
    return [...new Set(levels)].filter(Boolean).sort();
  }, [isAdminOrExaminer, teacherSubjectsData, classroomsData]);

  return (
    <Box>
      {canCreateQuizzes && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "auto",
              sm: "auto",
              md: "auto auto auto",
              lg: "auto auto auto",
            },
            gap: { xs: 2.5, sm: 2, md: 2, lg: 2.5 },
          }}
        >
          {overviewCard.map((card) => (
            <CustomOverviewCard
              key={card.id}
              title={card.text}
              icon={card.icon}
              onClick={() => navigate(`${card.link}`)}
            />
          ))}
        </Box>
      )}
      <Box sx={{ marginTop: "2em" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1em",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: "1rem", md: "1.25rem" },
              fontWeight: 700,
              letterSpacing: "-1px",
            }}
          >
            Recent Quizzes
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Select
              value={filterSubject}
              onChange={handleFilterSubjectChange}
              size="small"
              displayEmpty
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="all">All Subjects</MenuItem>
              {uniqueSubjects.map((subject) => (
                <MenuItem key={subject} value={subject}>
                  {subject}
                </MenuItem>
              ))}
            </Select>
            <Select
              value={filterLevel}
              onChange={handleFilterLevelChange}
              size="small"
              displayEmpty
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="all">All Levels</MenuItem>
              {uniqueLevels.map((level) => (
                <MenuItem key={level} value={level}>
                  {level}
                </MenuItem>
              ))}
            </Select>
            <Select
              value={filterType}
              onChange={handleFilterTypeChange}
              size="small"
              displayEmpty
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="quiz">Quiz</MenuItem>
              <MenuItem value="practice">Practice</MenuItem>
              <MenuItem value="duel">Duel</MenuItem>
            </Select>
            <Select
              value={sortBy}
              onChange={handleChangeSort}
              size="small"
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="date">Sort by: Date</MenuItem>
              <MenuItem value="subject">Sort by: Subject</MenuItem>
              <MenuItem value="level">Sort by: Level</MenuItem>
            </Select>
          </Box>
        </Box>

        {(isLoading || isFetching) && (
          <Box sx={{ width: "100%", mb: 2 }}>
            <LinearProgress />
          </Box>
        )}

        {quizzes.length === 0 && !isLoading && (
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary">
              No quizzes available yet
            </Typography>
          </Box>
        )}

        {quizzes.map((quiz) => (
          <CustomClickableQuestionsCard
            key={quiz.id}
            icon={QuizIcon}
            title={`${quiz.subject} — ${quiz.level}`}
            details={`${quiz.type}${quiz.is_offline ? " • Offline" : ""}`}
            date={formatDate(quiz.updated_at)}
            status={quiz.type}
            navigateTo={`view/${quiz.id}`}
            cardData={quiz}
          />
        ))}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "2em",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing {totalQuizzes > 0 ? startIndex : 0} to {endIndex} of {totalQuizzes} entries
          </Typography>

          <Pagination
            count={totalPages}
            page={page}
            onChange={handleChangePage}
            color="primary"
            shape="rounded"
            sx={(theme) => ({ color: theme.palette.mode === "dark" ? "#888" : "#CCCCCC" })}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default Quizzes;
